import { GiB, LLAMACPP_UBATCH, kvBytesPerSequence, type Inputs } from "./core.ts";
import type { ModelPreset } from "./models.ts";
import { isConsole, singleDevice } from "./hardware.ts";
import { reserveFor, vllmExpertPadding } from "./reserve.ts";

// ── Poids : ce qui est quantifié, et où ça se trouve ───────────────────────
// GGUF : tout le fichier est quantifié, embeddings compris ; les bits sont des moyennes mesurées.
// FP8, INT8, NVFP4, MXFP4, GPTQ / AWQ (à la volée par le moteur, ou checkpoint quantifié) : seules
// les couches linéaires le sont, l'embedding d'entrée et la tête de sortie restent en BF16. Sur un
// petit modèle au grand vocabulaire, c'est une bonne part du total (Qwen3.5 4B : 27 %, Gemma 4 12B :
// 17 %, Llama 3.1 8B : 13 %) : vLLM charge Llama 3.1 8B FP8 en environ 8,5 GiB, pas 7,5.

const GGUF_BITS = new Set([8.5, 6.5625, 5.7, 4.85, 3.9, 2.95, 2.1]);
/**
 * Disposition d'un fichier GGUF (règles de llama-quant.cpp) : la tête de sortie (ou la table
 * partagée qui en tient lieu) en Q6_K dans les formats « K », le reste à un nombre moyen de bits.
 * Moyennes calées sur les fichiers publiés des plus gros modèles, où la tête pèse peu : DeepSeek R1
 * et Kimi K2.6 (unsloth, DevQuasar) donnent 4,83 bits en Q4_K_M, 5,67 en Q5_K_M, 3,81 en Q3_K_M.
 * Contrôle sur 34 modèles (tailles exactes des fichiers, octobre 2026) : écart médian sous 1 %.
 */
const GGUF_LAYOUT: Record<number, { body: number; output: number; fallback: number }> = {
  8.5: { body: 8.5, output: 8.5, fallback: 8.5 }, 6.5625: { body: 6.5625, output: 6.5625, fallback: 8.5 },
  5.7: { body: 5.67, output: 6.5625, fallback: 6.4 }, 4.85: { body: 4.83, output: 6.5625, fallback: 5.98 },
  3.9: { body: 3.81, output: 6.5625, fallback: 4.85 },
  // 2 bits (catalogue, 14 octobre 2026) : Q2_K 2,92 bits hors tête sur 21 modèles (2,89 à 2,94 sur les gros),
  // IQ2_XXS 2,09 (Llama 3.1 405B, MiniMax M2.7). Matrice impaire : IQ4_NL (4,5 bits) dans les deux cas.
  2.95: { body: 2.92, output: 6.5625, fallback: 4.5 }, 2.1: { body: 2.09, output: 6.5625, fallback: 4.5 },
};
// fallback : une matrice dont la largeur d'entrée n'est pas un multiple de 256 ne peut pas prendre un
// format « K » (blocs de 256) ; llama-quant.cpp la passe au format à blocs de 32 le plus proche
// (Q3_K → Q4_0, Q4_K → Q5_0, Q5_K → Q5_1, Q6_K → Q8_0). Moyennes selon le mélange de chaque format
// (Q4_K_M : 84 % de Q4_K, 16 % de Q6_K). La tête de sortie passe de même en Q8_0.
/** Format qui ne quantifie que les couches linéaires (embeddings et tête de sortie en BF16). */
export const linearOnly = (bits: number) => bits > 0 && bits < 16 && !GGUF_BITS.has(bits);
/** Bits des formats FP4 publiés en checkpoint (NVFP4 de NVIDIA : 4,5 ; MXFP4 d'AMD : 4,25), voir WEIGHT_FORMATS. */
export const FP4_CHECKPOINT_BITS: ReadonlySet<number> = new Set([4.5, 4.25]);
/** Attention à latent unique (MLA de DeepSeek, Kimi, GLM-5, Mistral Large ; DeepSeek V4) : un groupe de
 *  cache sans têtes réparties entre GPU ni taille fixe. */
export const latentAttention = (m: Pick<ModelPreset, "kv">) => m.kv.some((g) => !g.tpHeads && !g.fixedBytes);
/** Éléments d'un tableau d'embedding : vocabulaire × largeur. */
export const vocabParams = (m: ModelPreset) => m.vocab * m.hidden;

/**
 * Milliards de paramètres chargés pour servir le modèle : sans les couches MTP (chargées seulement
 * en décodage spéculatif : vLLM les saute, voir get_spec_layer_idx_from_weight_name) ; en GGUF, sans
 * l'encodeur de vision ou d'audio, publié à part dans un fichier mmproj.
 */
export function loadedParams(m: ModelPreset, gguf = false): number {
  return m.params - (m.parts?.mtp ?? 0) - (gguf ? m.parts?.vision ?? 0 : 0);
}
/** Format GGUF (bits moyens mesurés par llama.cpp). */
export const isGgufBits = (bits: number) => GGUF_BITS.has(bits);

export function weightBytes(model: ModelPreset, bits: number): number {
  // Checkpoint natif : la taille exacte des fichiers, sans les couches MTP.
  if (bits === 0 && model.native) return model.native.bytes - (model.mtpBytes ?? 0);
  const b = bits || 16, P = loadedParams(model, GGUF_BITS.has(b)) * 1e9;
  const gguf = GGUF_LAYOUT[b];
  if (gguf) {
    // Experts gardés dans leur format natif (gpt-oss : MXFP4, que llama.cpp ne requantifie pas).
    const kept = model.ggufExpertBits && model.parts?.experts ? model.parts.experts * 1e9 : 0;
    const head = vocabParams(model);
    // Plafonnée à ce qui est chargé hors tête de sortie : si le nombre de paramètres est faux, le
    // recensement compte plus de matrices « impaires » que de paramètres chargés (Nemotron 3.5 Lightning,
    // total Hugging Face sans sa couche MTP : 30,9 G pour 30,2 G), la part au format principal devient
    // négative et Q6_K plus lourd que Q8_0 (build du 12 octobre 2026).
    const odd = Math.min(Math.max(0, (model.parts?.odd ?? 0) * 1e9 - kept), Math.max(0, P - kept - head));
    const output = model.hidden % 256 === 0 ? gguf.output : Math.max(gguf.output, 8.5);
    return kept * ((model.ggufExpertBits ?? 0) / 8) + (P - kept - odd) * (gguf.body / 8) + odd * (gguf.fallback / 8)
      + head * ((output - gguf.body) / 8);
  }
  if (!linearOnly(b)) return P * (b / 8);
  // Checkpoints FP4 publiés des modèles MLA : seuls les experts routés sont en FP4. L'attention (que ModelOpt
  // de NVIDIA et Quark d'AMD ne quantifient pas en MLA), les experts partagés, les couches denses et les
  // embeddings restent en BF16. Fichiers : nvidia/DeepSeek-R1-0528-NVFP4-v2, 664,8 G paramètres en FP4
  // (58 couches × 256 experts, plus ceux de la couche MTP) et 19,7 G en BF16 ; nvidia/GLM-5-NVFP4, 25,6 G en
  // BF16 ; amd/DeepSeek-R1-0528-MXFP4, 26,8 G. SGLang (InferenceX, octobre 2026) chargeait 4 à 13 % de plus
  // que le calcul « tout en FP4 ». En attention GQA, tout est quantifié (Llama 3.3 70B et Qwen3 235B NVFP4 :
  // seuls les embeddings en BF16) : la règle générale ci-dessous.
  if (FP4_CHECKPOINT_BITS.has(b) && model.parts?.experts && latentAttention(model)) {
    const experts = Math.min(model.parts.experts * 1e9, P);
    const tables = Math.min((model.parts.lookup ?? 0) * 1e9, P - experts); // tables Engram : au format, comme avant
    return (experts + tables) * (b / 8) + (P - experts - tables) * 2;
  }
  const E = Math.min(P / 2, vocabParams(model) * (model.tiedEmbeddings ? 1 : 2));
  return (P - E) * (b / 8) + E * 2;
}

/**
 * Bits des poids tels que le moteur les charge. llama.cpp lit un GGUF : le checkpoint MXFP4 de gpt-oss
 * y est le fichier de ggml-org (gpt-oss-20b-MXFP4.gguf : 11 549 Mio), experts gardés en MXFP4 et tout
 * le reste en Q8_0, soit la disposition Q8_0 du calculateur (ggufExpertBits) à 0,4 % près. Les
 * safetensors d'OpenAI gardent l'attention et les embeddings en BF16 : 13 124 Mio, 14 % de trop
 * (llama.cpp b6360 : 11 540 Mio chargés). La version d'Ollama, elle, les garde aussi (13 154 Mio).
 */
export const engineBits = (i: Pick<Inputs, "model" | "weightBits" | "layerSplit">) =>
  i.layerSplit && i.weightBits === 0 && i.model.native && i.model.ggufExpertBits ? 8.5 : i.weightBits;

/** Octets d'un tableau d'embedding (entrée, ou tête de sortie) dans le format choisi. */
function embedBytes(model: ModelPreset, bits: number, total: number): number {
  const b = bits || 16;
  const per = bits === 0 && model.native ? 2 : linearOnly(b) ? 2 : b / 8;
  return Math.min(total / 4, vocabParams(model) * per);
}

/** Octets des tables lues ligne par ligne (n-gram de Qwen, Engram de DeepSeek), au prorata des poids. */
export function lookupBytes(model: ModelPreset, bits: number, total: number): number {
  const lookup = model.parts?.lookup ?? 0;
  return lookup ? total * Math.min(1, lookup / loadedParams(model, GGUF_BITS.has(bits || 16))) : 0;
}

/**
 * Poids sur le GPU le plus chargé. Parallélisme tensoriel : tout est divisé. Pipeline : chaque étage
 * prend sa part des couches, le premier l'embedding d'entrée, le dernier la tête de sortie (une copie
 * si elle est partagée). llama.cpp (layerSplit) : couches réparties entre GPU, embedding d'entrée et
 * tables lues ligne par ligne laissés en RAM (voir embeddingsInRam), tête de sortie sur le dernier GPU.
 */
export function weightsPerGpu(i: Pick<Inputs, "model" | "weightBits" | "exactWeightBytes" | "layerSplit" | "target">, tp: number, pp: number): number {
  const bits = engineBits(i);
  const W = (i.exactWeightBytes ?? weightBytes(i.model, bits)) + expertPaddingBytes(i, bits, tp);
  const emb = embedBytes(i.model, bits, W);
  const tables = i.layerSplit ? lookupBytes(i.model, bits, W) : 0;
  const body = W - tables - emb * (i.model.tiedEmbeddings ? 1 : 2);
  // Console : CPU et GPU puisent dans le même tas, l'embedding d'entrée « en RAM » y est aussi.
  if (i.target && isConsole(i.target.gpu)) return W / (tp * pp);
  const stage = pp > 1 || i.layerSplit ? body / pp + emb : W;
  return stage / tp;
}

/** Octets ajoutés par l'arrondi des experts FP4 natifs que fait vLLM (reserve.ts, vllmExpertPadding). */
function expertPaddingBytes(i: Pick<Inputs, "model" | "layerSplit" | "target">, bits: number, tp: number): number {
  if (bits !== 0 || i.layerSplit || i.target?.engine !== "vllm" || !i.model.parts?.experts) return 0;
  const factor = vllmExpertPadding(i.model, i.target.gpu, tp);
  // Experts FP4 natifs : 4 bits et une échelle de 8 bits par bloc de 32, soit 4,25 bits.
  return factor > 1 ? i.model.parts.experts * 1e9 * (4.25 / 8) * (factor - 1) : 0;
}

/**
 * Ce qui reste en RAM de toute façon avec llama.cpp : l'embedding d'entrée, et les tables lues ligne
 * par ligne (n-gram de Qwen3.8-Flash-Next, ~30 Gio en Q4). llama.cpp les range avec l'entrée, côté
 * processeur (LLM_TENSOR_LAYER_INPUT, llama-arch.cpp) et lit celles de plus de 4 Gio à la demande
 * depuis le fichier (lazy read, llama-model-loader.cpp) : comptées en RAM quand même, par prudence.
 */
export function embeddingsInRam(i: Pick<Inputs, "model" | "weightBits" | "exactWeightBytes" | "layerSplit" | "target">): number {
  if (!i.layerSplit || (i.target && isConsole(i.target.gpu))) return 0;
  const bits = engineBits(i);
  const W = i.exactWeightBytes ?? weightBytes(i.model, bits);
  return embedBytes(i.model, bits, W) + lookupBytes(i.model, bits, W);
}

export type Result = {
  gpus: number;
  budgetGiB: number;
  /** Part de la mémoire retenue (celle du moteur visé, ou saisie). */
  utilization: number;
  weightsGiB: number;
  kvGiB: number;
  stateGiB: number;
  overheadGiB: number;
  /** Entraînement : gradients + optimiseur, et activations. */
  gradsOptimGiB: number;
  activationsGiB: number;
  totalGiB: number;
  freeGiB: number;
  status: "ok" | "tight" | "oom";
  /** Inférence : séquences max au contexte choisi (0 si les poids ne tiennent pas). */
  maxSequences: number;
  /** Inférence : contexte max pour le nombre de séquences choisi. */
  maxContext: number;
};

export function compute(i: Inputs): Result {
  const tp = Math.max(1, Math.floor(i.tp));
  const pp = Math.max(1, Math.floor(i.pp));
  const gpus = tp * pp;
  const seqs = Math.max(0, i.sequences);
  const ctx = Math.max(0, i.context);
  // Part utilisable et surcoût : ceux du moteur visé sur ce GPU (reserve.ts), sinon ceux saisis.
  const reserve = reserveFor(i, tp, pp);
  const budgetGiB = Math.max(0, i.vramGiB) * reserve.utilization;
  const P = loadedParams(i.model) * 1e9;

  let weights: number; // affecté dans les deux branches
  let kv = 0, state = 0, gradsOptim = 0, activations = 0;
  let maxSequences = 0, maxContext = 0;

  if (i.mode === "inference") {
    weights = weightsPerGpu(i, tp, pp);
    const statePerSeq = (i.model.stateBytesPerSeq ?? 0) / gpus;
    const pad = i.layerSplit ? LLAMACPP_UBATCH / Math.max(1, seqs) : 0;
    kv = kvBytesPerSequence(i.model, ctx, i.kvBytes, tp, pp, pad) * seqs;
    state = statePerSeq * seqs;

    // Capacité : ce qui reste après les poids et le surcoût, divisé par le coût d'une séquence.
    const room = budgetGiB * GiB - weights - reserve.overheadGiB * GiB;
    const perSeq = kvBytesPerSequence(i.model, ctx, i.kvBytes, tp, pp, pad) + statePerSeq;
    maxSequences = room > 0 && perSeq > 0 ? Math.floor(room / perSeq) : 0;
    // llama.cpp garde son lot de calcul en plus des fenêtres glissantes une fois pour toutes les
    // séquences, pas une fois chacune : avec plus de séquences, chacune coûte moins. Le coût total
    // croît quand même avec leur nombre, d'où une recherche dichotomique (sinon jusqu'à -40 % sur
    // Gemma 4 12B : 25 requêtes annoncées sur une RTX 4090 à 8K, 33 en réalité).
    if (i.layerSplit && maxSequences > 0) {
      const fits = (s: number) => s * (kvBytesPerSequence(i.model, ctx, i.kvBytes, tp, pp, LLAMACPP_UBATCH / s) + statePerSeq) <= room;
      let lo = maxSequences, hi = maxSequences * 2;
      while (fits(hi) && hi < 1 << 30) { lo = hi; hi *= 2; }
      while (hi - lo > 1) {
        const mid = Math.floor((lo + hi) / 2);
        if (fits(mid)) lo = mid; else hi = mid;
      }
      maxSequences = lo;
    }

    // Contexte max : recherche dichotomique (le coût croît avec le contexte, mais pas
    // linéairement à cause des fenêtres glissantes).
    if (room > 0 && seqs > 0) {
      const fits = (c: number) => seqs * (kvBytesPerSequence(i.model, c, i.kvBytes, tp, pp, pad) + statePerSeq) <= room;
      let lo = 0, hi = 1 << 24; // 16M tokens, au-delà de tout modèle actuel
      if (fits(hi)) maxContext = hi;
      else {
        while (hi - lo > 1) {
          const mid = (lo + hi) >>> 1;
          if (fits(mid)) lo = mid; else hi = mid;
        }
        maxContext = lo;
      }
    }
  } else {
    // AdamW en précision mixte : poids BF16 (2) + gradients BF16 (2) + copie FP32 (4)
    // + deux moments FP32 (8) = 16 octets par paramètre entraîné (ZeRO, Rajbhandari 2020).
    const shard = i.shardStates ? gpus : 1;
    const trained = i.mode === "full" ? P : P * i.loraFraction;
    // NF4 + double quantification ; bitsandbytes laisse l'embedding et la tête de sortie en BF16.
    const E = Math.min(P / 2, vocabParams(i.model) * (i.model.tiedEmbeddings ? 1 : 2));
    weights = (i.mode === "full" ? 2 * P : i.mode === "qlora" ? (P - E) * (4.127 / 8) + E * 2 : 2 * P) / shard;
    gradsOptim = (i.mode === "full" ? 14 * P : 16 * trained) / shard;

    // Activations BF16 avec FlashAttention : ~34·s·b·h octets par couche (Korthikanti
    // et al. 2022, sans le terme des scores d'attention). Avec recalcul complet, on ne
    // garde que l'entrée de chaque couche (2·s·b·h) plus une couche entière.
    const sbh = ctx * seqs * i.model.hidden;
    const perLayer = 34 * sbh;
    const layerActs = i.gradientCheckpointing ? i.model.layers * 2 * sbh + perLayer : i.model.layers * perLayer;
    const logits = ctx * seqs * i.model.vocab * 4; // logits FP32 pour la perte
    activations = layerActs + logits;
  }

  const overhead = reserve.overheadGiB * GiB;
  const total = weights + kv + state + gradsOptim + activations + overhead;
  const freeGiB = budgetGiB - total / GiB;
  const status: Result["status"] =
    freeGiB < 0 ? "oom" : freeGiB < Math.max(1, 0.05 * budgetGiB) ? "tight" : "ok";

  return {
    gpus,
    budgetGiB,
    weightsGiB: weights / GiB,
    kvGiB: kv / GiB,
    stateGiB: state / GiB,
    overheadGiB: reserve.overheadGiB,
    utilization: reserve.utilization,
    gradsOptimGiB: gradsOptim / GiB,
    activationsGiB: activations / GiB,
    totalGiB: total / GiB,
    freeGiB,
    status,
    maxSequences,
    maxContext,
  };
}

/** Tout de 1 à 8 (stations de travail à 3 ou 6 cartes), puis par serveurs de 8. */
export const GPU_COUNTS = [1, 2, 3, 4, 5, 6, 7, 8, 16, 24, 32, 48, 64, 96, 128];

/**
 * Répartition de n GPU : tensoriel sur la plus grande puissance de 2 (≤ 8) qui divise
 * n, car les têtes d'attention ne se coupent pas en 3 ; pipeline pour le reste
 * (3 GPU → 1 × 3, 6 → 2 × 3, 16 → 8 × 2). llama.cpp répartit aussi par couches.
 */
export function split(n: number) {
  let tp = 1;
  while (tp < 8 && n % (tp * 2) === 0) tp *= 2;
  return { tp, pp: n / tp };
}

/** Ordre de grandeur de la perte de qualité face au BF16 ; varie d'un modèle à l'autre. */
export function qualityHint(bits: number): string {
  if (bits >= 16) return "Reference quality";
  if (bits >= 8) return "Near lossless";
  if (bits >= 5.5) return "Very small loss";
  if (bits >= 4) return "Small loss";
  if (bits >= 3) return "Noticeable loss";
  return "Large loss";
}

/** Répartition de n GPU selon le moteur : llama.cpp par couches, les autres comme `split`. */
export const splitFor = (n: number, layerSplit?: boolean) => (layerSplit ? { tp: 1, pp: n } : split(n));

/** Plus petit nombre de GPU qui fait tenir la charge demandée. Une console, ou un Mac avec Transformers, ne se
 *  met pas en grappe : un seul. */
export function minGpus(i: Inputs): number | null {
  const counts = i.target && singleDevice(i.target.gpu, i.target.engine) ? [1] : GPU_COUNTS;
  return counts.find((n) => compute({ ...i, ...splitFor(n, i.layerSplit) }).status !== "oom") ?? null;
}
